import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, ClipboardList, Plus } from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { cantidad } from "../../lib/format";
import { Modal } from "../../components/Modal";
import {
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
} from "../../components/ui";
import {
  abrirCierre,
  listarCierresPorPeriodo,
  type AbrirCierrePayload,
} from "./api";
import { EstadoBadge, MESES } from "./estado";
import { listarSectores } from "../sectores/api";

const HOY = new Date();

export function CierresListPage(): JSX.Element {
  // `?mes=&anio=` lo usan los links del Inicio (cierre en HOLD, por facturar…).
  const [params] = useSearchParams();
  const mesUrl = Number(params.get("mes"));
  const anioUrl = Number(params.get("anio"));
  const [mes, setMes] = useState(mesUrl >= 1 && mesUrl <= 12 ? mesUrl : HOY.getMonth() + 1);
  const [anio, setAnio] = useState(anioUrl >= 2000 ? anioUrl : HOY.getFullYear());
  const [openForm, setOpenForm] = useState(false);

  const query = useQuery({
    queryKey: ["cierres", mes, anio],
    queryFn: () => listarCierresPorPeriodo(mes, anio),
  });
  const sectoresQ = useQuery({ queryKey: ["sectores"], queryFn: listarSectores });
  const nombreSector = (id: number | null): string =>
    id == null
      ? "Todos los sectores"
      : sectoresQ.data?.find((s) => s.id === id)?.nombre ?? "…";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cierres"
        subtitle="Cierre mensual de lecturas y facturación, por período."
        icon={ClipboardList}
      />

      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Mes"
          value={mes}
          onChange={(e) => setMes(Number(e.target.value))}
          containerClassName="w-44"
        >
          {MESES.map((nombre, idx) => (
            <option key={idx} value={idx + 1}>
              {nombre}
            </option>
          ))}
        </Select>
        <Input
          label="Año"
          type="number"
          min={2020}
          max={2100}
          value={anio}
          onChange={(e) => setAnio(Number(e.target.value))}
          containerClassName="w-28"
        />
        <Button icon={Plus} onClick={() => setOpenForm((v) => !v)}>
          Abrir cierre
        </Button>
      </div>

      {openForm && (
        <AbrirCierreForm
          mes={mes}
          anio={anio}
          onClose={() => setOpenForm(false)}
        />
      )}

      {query.isPending && <Cargando />}
      {query.isError && (
        <ErrorCarga
          error={query.error}
          mensaje="Error al cargar cierres."
          onReintentar={() => void query.refetch()}
        />
      )}

      {query.data && query.data.length === 0 && (
        <p className="text-sm text-neutral-600">
          Sin cierres para {MESES[mes - 1]} {anio}.
        </p>
      )}

      {query.data && query.data.length > 0 && (
        <TableWrap label="Cierres del período">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-100 text-xs font-medium uppercase tracking-wider text-neutral-600">
              <tr>
                <th scope="col" className="px-4 py-2 text-left">Periodo</th>
                <th scope="col" className="px-4 py-2 text-left">Sector</th>
                <th scope="col" className="px-4 py-2 text-left">Estado</th>
                <th scope="col" className="px-4 py-2 text-right">Lecturas</th>
                <th scope="col" className="px-4 py-2 text-right">Total facturado</th>
                <th scope="col" className="px-4 py-2 text-right">{""}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {query.data.map((c) => (
                <tr key={c.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    {`${String(c.mes).padStart(2, "0")}/${c.anio}`}
                  </td>
                  <td className="px-4 py-3 text-neutral-700">
                    {nombreSector(c.sector_id)}
                  </td>
                  <td className="px-4 py-3">
                    <EstadoBadge estado={c.estado} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {/* Antes de revisar las lecturas no hay esperadas: "—", no "0/0". */}
                    {c.lecturas_esperadas > 0
                      ? `${cantidad(c.lecturas_ingresadas)}/${cantidad(c.lecturas_esperadas)}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">
                    {formatCLP(c.total_facturado)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/cierres/${c.id}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
                    >
                      Detalle
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

interface AbrirFormProps {
  mes: number;
  anio: number;
  onClose: () => void;
}

function AbrirCierreForm({ mes, anio, onClose }: AbrirFormProps): JSX.Element {
  const qc = useQueryClient();
  const [sectorId, setSectorId] = useState<string>("");
  const sectoresQ = useQuery({ queryKey: ["sectores"], queryFn: listarSectores });
  const [observaciones, setObservaciones] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: AbrirCierrePayload) => abrirCierre(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cierres", mes, anio] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "No se pudo abrir el cierre");
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Abrir cierre · ${MESES[mes - 1]} ${anio}`}
      icon={CalendarDays}
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setError(null);
              mutation.mutate({
                mes,
                anio,
                sector_id: sectorId ? Number(sectorId) : null,
                observaciones: observaciones.trim() || null,
              });
            }}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Abriendo…" : "Abrir"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select
          label="Sector"
          value={sectorId}
          onChange={(e) => setSectorId(e.target.value)}
          hint="Un cierre por sector permite facturar cada sector en su fecha."
        >
          <option value="">Todos los sectores</option>
          {(sectoresQ.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.nombre}
            </option>
          ))}
        </Select>
        <Input
          label="Observaciones"
          type="text"
          maxLength={255}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
    </Modal>
  );
}
